import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';

// material-ui
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

// project-imports
import { stepUpStart } from 'api';
import { ApiError } from 'utils/axios';
import { redirectToExternal } from 'utils/redirect';
import { onStepUpRequired } from 'utils/step-up';

// ==============================|| STEP-UP DIALOG ||============================== //
//
// Mounted once, for the whole app. Any call answered `step_up_required` opens it. Confirming starts a
// fresh sign-in with prompt=login at UzDevid ID and returns to this page, where the action is repeated.

const StepUpDialog = () => {
  const intl = useIntl();
  const location = useLocation();

  const [open, setOpen] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      onStepUpRequired(() => {
        setError(null);
        setOpen(true);
      }),
    []
  );

  const close = () => {
    if (!busy) setOpen(false);
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);

    try {
      const { authorizeUrl } = await stepUpStart(`${location.pathname}${location.search}`);

      redirectToExternal(authorizeUrl);
    } catch (err) {
      setBusy(false);
      setError((err as ApiError).message || intl.formatMessage({ id: 'stepUp.error' }));
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>
        <FormattedMessage id="stepUp.title" />
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <FormattedMessage id="stepUp.text" />
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={close} disabled={busy}>
          <FormattedMessage id="common.cancel" />
        </Button>
        <Button variant="contained" onClick={confirm} disabled={busy}>
          <FormattedMessage id="stepUp.confirm" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StepUpDialog;
